'use client';

import { Radio as RadioPrimitive } from '@base-ui/react';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import {
  RadioChoiceCardFrameContext,
  useRadioChoiceCardGroupContext,
} from './radio-choice-card-context';

const frameClasses = [
  // Default
  'group flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border-field bg-surface-primary p-4 text-left outline-none transition-all duration-150 ease-out',
  // Hover, unselected only so the selected background always wins
  '[&:not([data-checked])]:hover:bg-surface-hover',
  // Focus
  'focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2',
  // Selected
  '[&[data-checked]:not([data-disabled])]:border-border-active data-[checked]:bg-surface-active',
  // Disabled
  'data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:border-border-disabled data-[disabled]:text-text-disabled',
  // Selected and disabled
  '[&[data-checked][data-disabled]]:border-border-active-disabled [&[data-checked][data-disabled]]:bg-surface-active-disabled',
  // With description
  'has-[[data-slot=radio-choice-card-description]]:py-3 has-[[data-slot=radio-choice-card-description]]:[&_[data-slot=radio-choice-card-title]]:typo-sm-semibold',
];

export type RadioChoiceCardFrameProps = Omit<
  RadioPrimitive.Root.Props,
  'ref' | 'render' | 'className' | 'children'
> & {
  className?: string;
  children?: ReactNode;
  /**
   * Keeps the title and the description on a single line each, with an ellipsis.
   * Set to `false` to let both wrap.
   */
  truncate?: boolean;
};

/**
 * Composition layer of the choice card: the clickable card surface.
 * Wraps Base UI `Radio.Root` as a `div`, so any content can live inside without
 * nesting a control in a control, and shares its state with the card parts.
 */
export const RadioChoiceCardFrame = forwardRef<HTMLDivElement, RadioChoiceCardFrameProps>(
  ({ truncate = true, className, children, ...props }, ref) => {
    const { error } = useRadioChoiceCardGroupContext();

    return (
      <RadioPrimitive.Root
        ref={ref}
        data-slot="radio-choice-card-frame"
        className={cn(
          frameClasses,
          error && 'border-border-error data-[checked]:border-border-error',
          className,
        )}
        {...props}
        render={(renderProps, state) => (
          <div {...renderProps}>
            <RadioChoiceCardFrameContext.Provider
              value={{ checked: state.checked, disabled: state.disabled, truncate }}>
              {children}
            </RadioChoiceCardFrameContext.Provider>
          </div>
        )}
      />
    );
  },
);
RadioChoiceCardFrame.displayName = 'RadioChoiceCardFrame';
